import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

const initialForm = {
  debtor_id: "",
  principal_amount: "",
  currency: "USD",
  loan_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  purpose: "",
};

const LoanForm = ({ onCreated }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [debtors, setDebtors] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";
    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [user]);

  useEffect(() => {
    const fetchDebtors = async () => {
      try {
        setLoadingRefs(true);
        const res = await api.get("/debtors");
        setDebtors(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        enqueueSnackbar("Échec du chargement des débiteurs", {
          variant: "error",
        });
        setDebtors([]);
      } finally {
        setLoadingRefs(false);
      }
    };

    fetchDebtors();
  }, [enqueueSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const { debtor_id, principal_amount, currency, loan_date, due_date, purpose } = form;

    if (!user?.agency_id) {
      enqueueSnackbar("Votre compte n’a pas d’agence assignée", {
        variant: "error",
      });
      return;
    }

    if (!debtor_id || !principal_amount || !currency) {
      enqueueSnackbar("Débiteur, montant et devise sont requis", {
        variant: "warning",
      });
      return;
    }

    const numericAmount = Number(principal_amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      enqueueSnackbar("Montant invalide", { variant: "warning" });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        debtor_id: Number(debtor_id),
        agency_id: Number(user.agency_id),
        principal_amount: numericAmount,
        currency: String(currency).trim().toUpperCase(),
        loan_date: loan_date || null,
        due_date: due_date || null,
        purpose: purpose.trim() || null,
      };

      const res = await api.post("/loans", payload);

      enqueueSnackbar("Prêt enregistré avec succès", {
        variant: "success",
      });

      setForm(initialForm);

      if (typeof onCreated === "function") {
        onCreated(res.data);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error || "Échec de l'enregistrement du prêt";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Nouveau prêt
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Agence :</strong>{" "}
        {assignedAgencyLabel || `Agence #${user?.agency_id || "non définie"}`}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Débiteur"
            name="debtor_id"
            value={form.debtor_id}
            onChange={handleChange}
            disabled={loadingRefs}
          >
            {debtors.map((debtor) => (
              <MenuItem key={debtor.id} value={String(debtor.id)}>
                {debtor.full_name} {debtor.phone ? `— ${debtor.phone}` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Montant"
            name="principal_amount"
            type="number"
            value={form.principal_amount}
            onChange={handleChange}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            label="Devise"
            name="currency"
            value={form.currency}
            onChange={handleChange}
          >
            <MenuItem value="USD">USD</MenuItem>
            <MenuItem value="CDF">CDF</MenuItem>
            <MenuItem value="RMB">RMB</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Date du prêt"
            name="loan_date"
            type="date"
            value={form.loan_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date d’échéance"
            name="due_date"
            type="date"
            value={form.due_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Objet / motif"
            name="purpose"
            value={form.purpose}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </Box>
    </Paper>
  );
};

export default LoanForm;